/**
 * Linked Variants Swatches — swatches.js
 * Ürün sayfası ve koleksiyon kartları için swatch etkileşim mantığı.
 * Harici bağımlılık yok, vanilla JS, ~4KB gzip.
 */

(function () {
  'use strict';

  // ─── Sabitler ────────────────────────────────────────────────────────────────
  const CSS_PREFIX = 'lv-';
  const WRAPPER_SEL = `.${CSS_PREFIX}swatches-wrapper`;
  const SWATCH_SEL = `.${CSS_PREFIX}swatch`;
  const ITEM_SEL = `.${CSS_PREFIX}swatch-item`;
  const ACTIVE_CLASS = `${CSS_PREFIX}swatch-item--active`;
  const OOS_CLASS = `${CSS_PREFIX}swatch--oos`;
  const TOOLTIP_SEL = `.${CSS_PREFIX}tooltip`;
  const CARD_SEL = `.${CSS_PREFIX}card-swatches`;

  // ─── Yardımcı fonksiyonlar ──────────────────────────────────────────────────

  /**
   * Güvenli JSON parse — hata durumunda null döner
   */
  function safeParse(str) {
    try { return JSON.parse(str); } catch { return null; }
  }

  /**
   * CSS değişkenlerini wrapper element'e uygula
   * Block ayarlarından data-attribute'ları okur
   */
  function applySwatchVars(wrapper) {
    const size = wrapper.dataset.swatchSize || 'medium';
    const shape = wrapper.dataset.swatchShape || 'circle';
    const border = wrapper.dataset.swatchBorder || 'ring';

    const sizeMap = { small: '20px', medium: '28px', large: '36px' };
    const radiusMap = { circle: '50%', square: '2px', rounded: '6px' };

    wrapper.style.setProperty('--lv-size', sizeMap[size] || '28px');
    wrapper.style.setProperty('--lv-radius', radiusMap[shape] || '50%');
    wrapper.style.setProperty('--lv-border-style', border);
  }

  // ─── Tooltip yönetimi ────────────────────────────────────────────────────────

  let activeTooltip = null;

  function showTooltip(item, tooltip) {
    if (activeTooltip && activeTooltip !== tooltip) {
      hideTooltip(activeTooltip);
    }
    tooltip.classList.add('lv-tooltip--visible');
    activeTooltip = tooltip;
  }

  function hideTooltip(tooltip) {
    tooltip.classList.remove('lv-tooltip--visible');
    if (activeTooltip === tooltip) activeTooltip = null;
  }

  function bindTooltips(wrapper) {
    const items = wrapper.querySelectorAll(ITEM_SEL);
    items.forEach(function (item) {
      const tooltip = item.querySelector(TOOLTIP_SEL);
      if (!tooltip) return;

      item.addEventListener('mouseenter', function () { showTooltip(item, tooltip); });
      item.addEventListener('mouseleave', function () { hideTooltip(tooltip); });
      item.addEventListener('focus', function () { showTooltip(item, tooltip); }, true);
      item.addEventListener('blur', function () { hideTooltip(tooltip); }, true);
    });
  }

  // ─── Ürün kartı hover: resim önizleme ───────────────────────────────────────

  function bindCardSwatchHover(cardWrapper) {
    const swatches = cardWrapper.querySelectorAll(SWATCH_SEL);
    if (!swatches.length) return;

    // Kartın en yakın product-card container'ını bul
    const card = cardWrapper.closest('[data-product-card], .card-wrapper, .product-card');
    if (!card) return;

    const imgEl = card.querySelector('img.motion-reduce, img.card__image, .card__media img');

    swatches.forEach(function (swatch) {
      const previewImg = swatch.dataset.previewImage;
      const originalSrc = imgEl ? imgEl.src : null;

      if (!previewImg || !imgEl) return;

      swatch.addEventListener('mouseenter', function () {
        imgEl.src = previewImg;
      });

      swatch.addEventListener('mouseleave', function () {
        if (originalSrc) imgEl.src = originalSrc;
      });
    });
  }

  // ─── Stok dışı sıralama ─────────────────────────────────────────────────────

  function reorderOutOfStock(wrapper) {
    const behavior = wrapper.dataset.outOfStock || 'strikethrough';
    if (behavior !== 'end') return;

    const list = wrapper.querySelector(`.${CSS_PREFIX}swatches-list`);
    if (!list) return;

    const items = Array.from(list.querySelectorAll(ITEM_SEL));
    const inStock = items.filter(function (i) { return !i.classList.contains(`${CSS_PREFIX}swatch-item--oos`); });
    const oos = items.filter(function (i) { return i.classList.contains(`${CSS_PREFIX}swatch-item--oos`); });

    inStock.concat(oos).forEach(function (item) { list.appendChild(item); });
  }

  // ─── Swatch yükleme animasyonu (IntersectionObserver) ───────────────────────

  function observeWrappers() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: hepsini hemen göster
      document.querySelectorAll(WRAPPER_SEL).forEach(initWrapper);
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          initWrapper(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px' });

    document.querySelectorAll(WRAPPER_SEL).forEach(function (el) {
      observer.observe(el);
    });
  }

  // ─── Wrapper başlatma ────────────────────────────────────────────────────────

  function initWrapper(wrapper) {
    if (wrapper.dataset.lvInit) return;
    wrapper.dataset.lvInit = '1';

    applySwatchVars(wrapper);
    bindTooltips(wrapper);
    reorderOutOfStock(wrapper);

    // Kart ise hover bağla
    if (wrapper.classList.contains(`${CSS_PREFIX}card-swatches`)) {
      bindCardSwatchHover(wrapper);
    }

    // Wrapper'ı görünür yap (CSS opacity transition)
    wrapper.classList.add('lv-swatches-wrapper--ready');
  }

  // ─── Koleksiyon kartı swatchları ─────────────────────────────────────────────
  // card-product snippet'e enjekte edilen .lv-card-swatches elementleri için

  function initCardSwatches() {
    document.querySelectorAll(CARD_SEL).forEach(function (el) {
      if (el.dataset.lvInit) return;
      el.dataset.lvInit = '1';
      bindCardSwatchHover(el);
      bindTooltips(el);
    });
  }

  // ─── Klavye erişilebilirliği ─────────────────────────────────────────────────

  function bindKeyboard(wrapper) {
    const swatches = wrapper.querySelectorAll(`${SWATCH_SEL}[href]`);
    swatches.forEach(function (swatch) {
      swatch.setAttribute('tabindex', '0');
      swatch.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          window.location.href = swatch.href;
        }
      });
    });
  }

  // ─── Sayfa geçişi sonrası yeniden init (SPA/AJAX temaları için) ─────────────
  // Dawn ve bazı temalar history.pushState kullanır

  function listenToPageChanges() {
    // Shopify section rendering API ve AJAX cart event'lerini dinle
    document.addEventListener('shopify:section:load', function (e) {
      const section = e.target;
      section.querySelectorAll(WRAPPER_SEL).forEach(initWrapper);
    });

    // Custom event: tema AJAX navigasyon yaptıktan sonra tetikler
    document.addEventListener('lv:refresh', function () {
      observeWrappers();
      initCardSwatches();
    });
  }

  // ─── Global API ─────────────────────────────────────────────────────────────

  window.LinkedVariants = {
    init: function () {
      observeWrappers();
      initCardSwatches();
      listenToPageChanges();
    },
    refresh: function () {
      document.querySelectorAll(WRAPPER_SEL).forEach(function (el) {
        delete el.dataset.lvInit;
      });
      observeWrappers();
    },
  };

  // ─── Otomatik başlat ─────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.LinkedVariants.init);
  } else {
    window.LinkedVariants.init();
  }

})();
